import Keycloak from 'keycloak-js';

// Check if we're in development mode and if the server is accessible
const isDevelopment = import.meta.env.DEV;

// Keycloak configuration from environment variables
// Production: auth.enlixy.com / realm: enlixy
// Staging: auth.enlixy-test.com / realm: enlixy-test
const KEYCLOAK_CONFIG = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.enlixy.com',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'enlixy',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'performance-cockpit-react',
};

// Create a singleton Keycloak instance to prevent double initialization
let keycloakInstance: Keycloak | null = null;

const getKeycloakInstance = (): Keycloak => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak(KEYCLOAK_CONFIG);
    
    // Disable problematic storage access methods
    if (keycloakInstance) {
      // Override storage access methods to prevent requestStorageAccess errors
      const originalUpdateToken = keycloakInstance.updateToken?.bind(keycloakInstance);
      if (originalUpdateToken) {
        keycloakInstance.updateToken = (minValidity?: number) => {
          try {
            return originalUpdateToken(minValidity);
          } catch (error) {
            console.warn('Token update failed, attempting alternative method:', error);
            return Promise.resolve(false);
          }
        };
      }
    }
    
    // Add event listeners for debugging (after instance is created)
    keycloakInstance.onReady = (authenticated: boolean) => {
      console.log('🔐 Keycloak ready. Authenticated:', authenticated);
      // Dispatch custom event after a small delay to ensure state is fully updated
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('keycloak-ready'));
      }, 100);
    };

    keycloakInstance.onAuthSuccess = () => {
      console.log('🔐 Authentication successful');
    };

    keycloakInstance.onAuthError = (error: any) => {
      console.error('🔐 Authentication error:', error);
      if (isDevelopment) {
        console.warn('Keycloak authentication failed in development mode. This might be due to server connectivity issues.');
      }
    };

    keycloakInstance.onAuthRefreshSuccess = () => {
      console.log('🔐 Token refresh successful');
    };

    keycloakInstance.onAuthRefreshError = () => {
      console.error('🔐 Token refresh failed');
    };

    keycloakInstance.onTokenExpired = () => {
      console.log('🔐 Token expired');
    };
  }
  
  return keycloakInstance;
};

export default getKeycloakInstance();
export { KEYCLOAK_CONFIG, isDevelopment };
